import { Fragment, useState } from 'react';
import { Box, Flex, Image, Text } from '@chakra-ui/react';
import { ChevronDownIcon, TriangleDownIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import AccountRow, { accountIsConverted, accountRateSources } from './AccountRow';
import RateSourceTooltip from '../shared/RateSourceTooltip';
import { mergeRateSources } from '../../utils/rateSources';
import { isDebtAccount } from '../../utils/accountTypes';
import { computeDeltaPct } from '../../utils/movementBuckets';
import { nestAccounts, isSettledLoan } from '../../utils/subAccounts';
import { signedForTotal } from '../../utils/accountGrouping';
import DeltaPill from './DeltaPill';

// Add or remove an id from a Set held in state.
const toggleIn = (setter, id) => setter((prev) => {
  const next = new Set(prev);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
});

function formatNumber(n) {
  return Math.round(n || 0).toLocaleString('en-US');
}

/**
 * One collapsible group of accounts ("Debit", "Credit", "Stocks", ...).
 * Renders the group header, the rows, and a totals footer.
 * Inactive accounts are hidden by default behind a "show inactive…" button.
 *
 * `label` and `logo` name a group by institution; `mixed` says the group holds
 * debt and assets together, so its total counts the debt against it.
 */
export default function AccountGroupSection({
  group,
  label,
  logo,
  mixed = false,
  accounts,
  accountStats,
  roiByAccount,
  currency,
  approx = false,
  convert = null,
  multiCurrency = false,
  perCurrencyAccountStats,
  selectedAccountId,
  onAccountClick,
  freshness,
}) {
  const { t } = useTranslation('accounts');
  const [isOpen, setIsOpen] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  // Parents whose sub-accounts are folded, and people whose settled loans show.
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [showSettled, setShowSettled] = useState(() => new Set());

  const activeAccounts = accounts.filter(a => a.is_active !== false);
  const inactiveAccounts = accounts.filter(a => a.is_active === false);

  if (!accounts || accounts.length === 0) return null;

  const visibleAccounts = showInactive ? accounts : activeAccounts;

  // "Todas": the group footer is one subtotal per currency held in the group,
  // each with its own delta — summed from the same per-currency lines the rows
  // show, so the footer reconciles with them.
  // A debt counts against a mixed group; elsewhere every row adds as shown.
  const signOf = (a) => (mixed ? Math.sign(signedForTotal(a, 1)) : 1);
  const currencyFooters = multiCurrency ? (() => {
    const byCur = {};
    visibleAccounts.forEach(a => {
      const sign = signOf(a);
      (perCurrencyAccountStats?.get(a.id) || []).forEach(l => {
        const e = byCur[l.currency] || { total: 0, prevTotal: 0, direction: 0 };
        e.total += sign * l.balance;
        e.prevTotal += sign * l.prevBalance;
        e.direction += sign * (l.sparkline?.length >= 2 ? l.sparkline[l.sparkline.length - 1] - l.sparkline[0] : 0);
        byCur[l.currency] = e;
      });
    });
    return Object.entries(byCur).map(([cur, v]) => ({ currency: cur, ...v }));
  })() : null;
  const isDebtGroupFooter = !mixed && isDebtAccount(group);

  const groupTotal = visibleAccounts.reduce(
    (s, a) => s + signOf(a) * (accountStats.get(a.id)?.balance || 0),
    0
  );
  // MI-329: "≈" only when one of its accounts holds another currency.
  const groupIsConverted = approx && visibleAccounts.some(
    a => accountIsConverted(a, perCurrencyAccountStats?.get(a.id), convert)
  );

  // Group delta = simple average of per-account deltas (skip nulls). A mixed
  // group cannot average a debt's delta with an asset's, so it measures its
  // own total against where that total started.
  const deltaParts = visibleAccounts
    .map(a => accountStats.get(a.id))
    .filter(s => s && s.delta_pct != null);
  const mixedStart = visibleAccounts.reduce((s, a) => {
    const st = accountStats.get(a.id);
    return s + signOf(a) * (st?.sparkline?.length ? st.sparkline[0] : (st?.balance || 0));
  }, 0);
  const groupDelta = mixed
    ? computeDeltaPct(groupTotal, mixedStart)
    : deltaParts.length > 0
      ? deltaParts.reduce((s, p) => s + p.delta_pct, 0) / deltaParts.length
      : null;

  // Polarity from the group's signed direction — sum of (last − first) across
  // accounts in the group. Using `delta_pct >= 0` is unsafe because dividing
  // two negative balances flips the sign. For debt groups (Credit/Loan), the
  // accounts are abs'd in stats so an increasing sum means more debt = bad.
  const groupDirection = visibleAccounts.reduce((s, a) => {
    const sp = accountStats.get(a.id)?.sparkline || [];
    if (sp.length < 2) return s;
    return s + signOf(a) * (sp[sp.length - 1] - sp[0]);
  }, 0);
  const isDebtGroup = !mixed && isDebtAccount(group);
  const isNeutralGroup = Math.abs(groupDirection) < 0.005;
  const groupDeltaIsGood = (groupDelta == null || isNeutralGroup)
    ? null
    : (isDebtGroup ? groupDirection < 0 : groupDirection > 0);

  return (
    <Box mb={6}>
      <Flex
        as="button"
        type="button"
        align="center"
        gap="10px"
        mb={2}
        w="100%"
        onClick={() => setIsOpen(o => !o)}
        cursor="pointer"
        _hover={{ opacity: 0.8 }}
      >
        {/* Copilot's group title: a small filled caret and the name in
            title case, a step above the rows. */}
        <TriangleDownIcon
          color="redesign.text.muted"
          boxSize="9px"
          transform={isOpen ? 'rotate(0deg)' : 'rotate(-90deg)'}
          transition="transform 0.15s"
        />
        {logo && <Image src={logo} alt="" boxSize="18px" borderRadius="sm" />}
        <Text fontSize="17px" fontWeight={600} color="redesign.heading">
          {label ?? t(`categories.${group}`, { defaultValue: group })}
        </Text>
      </Flex>

      {/* Sub-accounts (holdings, loans) sit indented under their parent; the
          footer below still totals every row's own balance. */}
      {isOpen && nestAccounts(visibleAccounts).map(({ account: a, children }) => {
        const rowProps = (acc) => ({
          account: acc,
          stats: accountStats.get(acc.id),
          roi: roiByAccount?.get(acc.id),
          currency,
          approx,
          convert,
          multiCurrency,
          perCurrencyStats: perCurrencyAccountStats?.get(acc.id),
          isSelected: selectedAccountId === acc.id,
          onClick: onAccountClick,
          freshness: freshness?.get(acc.id),
        });
        const expanded = !collapsed.has(a.id);
        const settled = children.filter(isSettledLoan);
        const open = children.filter(c => !isSettledLoan(c));
        return (
          <Fragment key={a.id}>
            <AccountRow
              {...rowProps(a)}
              childCount={children.length}
              expanded={expanded}
              onToggle={() => toggleIn(setCollapsed, a.id)}
            />
            {expanded && open.map(c => <AccountRow key={c.id} {...rowProps(c)} nested />)}
            {expanded && settled.length > 0 && (
              <Box
                as="button"
                type="button"
                w="100%"
                py={1.5}
                pl={10}
                textAlign="left"
                fontSize="xs"
                color="redesign.text.muted"
                _hover={{ color: 'redesign.heading' }}
                onClick={() => toggleIn(setShowSettled, a.id)}
              >
                <ChevronDownIcon
                  boxSize={3}
                  mr={1}
                  transform={showSettled.has(a.id) ? 'none' : 'rotate(-90deg)'}
                  transition="transform 0.15s"
                />
                {t('groupSection.settledLoans', { count: settled.length })}
              </Box>
            )}
            {expanded && showSettled.has(a.id) && settled.map(c => <AccountRow key={c.id} {...rowProps(c)} nested />)}
          </Fragment>
        );
      })}

      {isOpen && inactiveAccounts.length > 0 && (
        <Box
          as="button"
          type="button"
          w="100%"
          mt={1}
          py={2}
          px={2}
          textAlign="center"
          fontSize="xs"
          color="redesign.text.muted"
          _hover={{ color: 'redesign.heading' }}
          onClick={() => setShowInactive(v => !v)}
        >
          {showInactive
            ? t('groupSection.hideClosed', { count: inactiveAccounts.length })
            : t('groupSection.showClosed', { count: inactiveAccounts.length })}
        </Box>
      )}

      {isOpen && multiCurrency ? (
        <Flex direction="column" align="flex-end" gap={1} mt={2} pt={2} px={2} borderTop="1px solid" borderColor="redesign.border">
          {currencyFooters.map(f => {
            const neutral = Math.abs(f.direction) < 0.005;
            const pct = computeDeltaPct(f.total, f.prevTotal);
            const good = (pct == null || neutral) ? null : (isDebtGroupFooter ? f.direction < 0 : f.direction > 0);
            return (
              <Flex key={f.currency} align="center" gap={3}>
                <Flex w="86px" justify="flex-end" flexShrink={0}>
                  <DeltaPill pct={pct} good={neutral ? null : good} up={f.direction > 0} />
                </Flex>
                <Text fontSize="15px" fontWeight={600} color="redesign.heading" minW="132px" textAlign="right">
                  <Text as="span" fontSize="10px" verticalAlign="0.35em" mr="3px" color="redesign.text.muted">{f.currency}</Text>
                  {formatNumber(f.total)}
                </Text>
              </Flex>
            );
          })}
        </Flex>
      ) : isOpen && <Flex
        align="center"
        justify="flex-end"
        gap={3}
        mt={2}
        pt={3}
        px={2}
        borderTop="1px solid"
        borderColor="redesign.border"
      >
        <DeltaPill pct={groupDelta} good={groupDeltaIsGood} up={groupDirection > 0} />
        <RateSourceTooltip
          sources={mergeRateSources(...visibleAccounts.map(a => accountRateSources(
            a, perCurrencyAccountStats?.get(a.id), convert)))}
          isDisabled={!groupIsConverted}
        >
        <Text fontSize="15px" fontWeight={600} color="redesign.heading">
          {groupIsConverted && <Text as="span" mr={0.5}>≈</Text>}
          <Text as="span" fontSize="10px" verticalAlign="0.35em" mr="3px" color="redesign.text.muted">{currency}</Text>
          {formatNumber(groupTotal)}
        </Text>
        </RateSourceTooltip>
      </Flex>}
    </Box>
  );
}
